/**
 * Items admin tab — mark items critical / override their lead-time.
 *
 * Audience: tags.manage capability (vendor + platform). The Inventory
 * Optimizer report reads both fields:
 *   - is_critical               keeps min/max unchanged for the item
 *                              (the report won't recommend new levels)
 *   - lead_time_override_days   overrides the smartbox3 value when it
 *                              is missing/wrong; controls the lead-time
 *                              bucket that drives target days-on-hand.
 *
 * Edits hit /api/admin/items/{id}/... and the server records who set
 * what + when (visible in tooltips on the row).
 */
import React, { useCallback, useEffect, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { fmtUtc } from "../../lib/format";
import {
  fetchItemsAdmin,
  setItemCritical,
  setItemLeadTime,
  type ItemMetaRow,
  type ItemsPage,
} from "../../services/adminApi";

const PAGE_SIZE = 100;

export const ItemsTab: React.FC = () => {
  const { token } = useAuth();
  const [page, setPage] = useState<ItemsPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [onlyCritical, setOnlyCritical] = useState(false);
  const [onlyOverridden, setOnlyOverridden] = useState(false);
  const [offset, setOffset] = useState(0);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const p = await fetchItemsAdmin(token, {
        q: q || undefined,
        only_critical: onlyCritical,
        only_overridden: onlyOverridden,
        limit: PAGE_SIZE,
        offset,
      });
      setPage(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, q, onlyCritical, onlyOverridden, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyEdit = (updated: ItemMetaRow): void => {
    if (!page) return;
    setPage({
      ...page,
      items: page.items.map((i) =>
        i.item_id === updated.item_id ? updated : i,
      ),
    });
  };

  const onSearchSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setOffset(0);
    setQ(qInput.trim());
  };

  return (
    <div className="space-y-3 max-w-5xl">
      {/* Filter bar */}
      <form
        onSubmit={onSearchSubmit}
        className="flex flex-wrap items-end gap-2 bg-secondary-50 border border-secondary-200 rounded-md p-3"
      >
        <label className="text-xs text-secondary-600 flex-1 min-w-[200px]">
          <div>Search SKU / name / OEM #</div>
          <input
            type="text"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="e.g. valve, 0740, plunger"
            className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
          />
        </label>
        <Button type="submit" size="sm">
          Search
        </Button>
        <label className="text-xs text-secondary-700 flex items-center gap-1.5 ml-2">
          <input
            type="checkbox"
            checked={onlyCritical}
            onChange={(e) => {
              setOffset(0);
              setOnlyCritical(e.target.checked);
            }}
          />
          Critical only
        </label>
        <label className="text-xs text-secondary-700 flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={onlyOverridden}
            onChange={(e) => {
              setOffset(0);
              setOnlyOverridden(e.target.checked);
            }}
          />
          With lead-time override
        </label>
      </form>

      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {/* Header */}
      {page && (
        <div className="flex items-center justify-between text-xs text-secondary-600">
          <span>
            <strong>{page.total.toLocaleString()}</strong> item
            {page.total === 1 ? "" : "s"}
            {q && <> matching "{q}"</>}
            {onlyCritical && " · critical only"}
            {onlyOverridden && " · with override"}
            {page.total > 0 && (
              <>
                {" "}
                — showing {offset + 1}–
                {Math.min(offset + page.items.length, page.total)}
              </>
            )}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0 || loading}
            >
              ← Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= page.total || loading}
            >
              Next →
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-secondary-50 text-secondary-700 uppercase">
            <tr>
              <th className="text-left px-3 py-2">SKU / Name</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-right px-3 py-2">Unit price</th>
              <th className="text-right px-3 py-2">Lead time</th>
              <th className="text-left px-3 py-2">Override</th>
              <th className="text-center px-3 py-2">Critical</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {loading && !page ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-secondary-500">
                  loading…
                </td>
              </tr>
            ) : page && page.items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-secondary-500">
                  No items match those filters.
                </td>
              </tr>
            ) : (
              page?.items.map((item) => (
                <ItemRow key={item.item_id} item={item} onChange={applyEdit} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ItemsTab;

const ItemRow: React.FC<{
  item: ItemMetaRow;
  onChange: (next: ItemMetaRow) => void;
}> = ({ item, onChange }) => {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overrideInput, setOverrideInput] = useState<string>(
    item.lead_time_override_days != null
      ? String(item.lead_time_override_days)
      : "",
  );

  // Re-sync the input when the row prop changes after save.
  useEffect(() => {
    setOverrideInput(
      item.lead_time_override_days != null
        ? String(item.lead_time_override_days)
        : "",
    );
  }, [item.lead_time_override_days]);

  const onToggleCritical = async (next: boolean): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await setItemCritical(token, item.item_id, next);
      onChange(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onSaveOverride = async (): Promise<void> => {
    if (!token) return;
    const raw = overrideInput.trim();
    let days: number | null = null;
    if (raw !== "") {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0 || n > 3650 || !Number.isInteger(n)) {
        setError("override must be an integer between 0 and 3650");
        return;
      }
      days = n;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await setItemLeadTime(token, item.item_id, days);
      onChange(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onClearOverride = async (): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await setItemLeadTime(token, item.item_id, null);
      onChange(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const overrideChanged =
    overrideInput !==
    (item.lead_time_override_days != null
      ? String(item.lead_time_override_days)
      : "");

  return (
    <tr className="hover:bg-secondary-50 align-top">
      <td className="px-3 py-2">
        <div className="font-mono font-semibold">{item.sku ?? "—"}</div>
        {item.name && (
          <div className="text-secondary-500 text-[11px]">{item.name}</div>
        )}
        {item.oem_no && (
          <div className="text-secondary-400 text-[10px]">OEM: {item.oem_no}</div>
        )}
        {error && (
          <div className="text-[11px] text-error-700 mt-1">{error}</div>
        )}
      </td>
      <td className="px-3 py-2 text-secondary-600">
        {item.category_name ?? "—"}
      </td>
      <td className="px-3 py-2 text-right font-mono">
        {item.unit_price != null
          ? `$${item.unit_price.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}`
          : "—"}
      </td>
      <td className="px-3 py-2 text-right font-mono">
        <span title="From smartbox3 md_items.leadTime">{item.lead_time_days}d</span>
        {item.lead_time_override_days != null && (
          <div className="text-[10px] text-warning-700">
            effective {item.lead_time_override_days}d
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <input
            type="text"
            inputMode="numeric"
            value={overrideInput}
            onChange={(e) =>
              setOverrideInput(e.target.value.replace(/[^\d]/g, ""))
            }
            placeholder="days"
            className="w-16 border border-secondary-300 rounded px-1.5 py-0.5 text-xs font-mono"
            disabled={busy}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => void onSaveOverride()}
            disabled={busy || !overrideChanged}
            title="Save override"
          >
            Save
          </Button>
          {item.lead_time_override_days != null && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void onClearOverride()}
              disabled={busy}
              title="Clear override (use source value)"
            >
              ✕
            </Button>
          )}
        </div>
        {item.lead_time_set_by && (
          <div
            className="text-[10px] text-secondary-500 mt-0.5"
            title={`${item.lead_time_set_by} · ${fmtUtc(item.lead_time_set_utc)}`}
          >
            by {item.lead_time_set_by}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-center">
        <input
          type="checkbox"
          checked={item.is_critical}
          onChange={(e) => void onToggleCritical(e.target.checked)}
          disabled={busy}
          className="h-4 w-4"
        />
        {item.critical_set_by && (
          <div
            className="text-[10px] text-secondary-500 mt-0.5"
            title={`${item.critical_set_by} · ${fmtUtc(item.critical_set_utc)}`}
          >
            by {item.critical_set_by}
          </div>
        )}
      </td>
    </tr>
  );
};
